// client-shared
import { storage } from '@goldblack/client-storage';

function setNavigation(props) {
    storage.transience.setState('main.containers', [props]);
}

function addNavigation(props) {
    storage.transience.addState('main.containers', props);
}

function removeNavigation() {
    storage.transience.popState('main.containers');
}

function pushNavigation(props) {
    const current = storage.transience.getValue('main.containers');
    storage.transience.setState('main.stack', current[0]);
    setNavigation(props);
}

function popNavigation() {
    const props = storage.transience.getValue('main.stack');
    setNavigation(props);
    storage.transience.setState('main.stack', null);
}

function createAdminProp() {
    const userSession = storage.transience.getValue('user');
    const config = storage.localStorage.getValue('config');
    return { path: 'admin', user: userSession, config: config?.data };
}

function createHomeProp() {
    const config = storage.localStorage.getValue('config');
    return { path: 'home', config: config?.data };
}

function createMessageProp() {
    const userSession = storage.transience.getValue('user');
    const config = storage.localStorage.getValue('config');
    return { path: 'messages', user: userSession, config: config?.data };
}

function createSettingProp() {
    const userSession = storage.transience.getValue('user');
    const config = storage.localStorage.getValue('config');
    return { path: 'settings', user: userSession, config: config?.data };
}

function createExploreProp() {
    const config = storage.localStorage.getValue('config');
    return { path: 'explore', config: config?.data };
}

function createProfileProp(user, openPostId = null) {
    const userSession = storage.transience.getValue('user');
    const config = storage.localStorage.getValue('config');
    return { path: 'profile', user: userSession, target: user, openPostId, config: config?.data };
}

function createSearchProp() {
    const config = storage.localStorage.getValue('config');
    return { path: 'search', config: config?.data };
}

function createUserListProp(
    user,
    type,
    filters = null,
    title = null,
    headMinHeight = null,
    onSelect = null,
    exclude = null,
) {
    const config = storage.localStorage.getValue('config');
    return {
        path: 'userlist',
        user,
        type,
        filters,
        title,
        headMinHeight,
        onSelect,
        exclude,
        config: config?.data,
    };
}

function createTrendingProp() {
    const config = storage.localStorage.getValue('config');
    return { path: 'trending', config: config?.data };
}

function createLegalProp(props) {
    const config = storage.localStorage.getValue('config');
    return { path: 'legal', config: config?.data, ...props };
}

function createConversationProp(peerId, peer, onClose) {
    const userSession = storage.transience.getValue('user');
    const config = storage.localStorage.getValue('config');
    return { path: 'conversation', peerId, peer, user: userSession, config: config?.data, onClose };
}

// ===== deep links =====

// URL segments → deep link target:
//   /@user            → profile
//   /@user/<postId>   → profile with that post opened
//   /messages/@user   → conversation with that user
//   /<tab>            → tab (recommend, search, explore, trending, messages, settings)
function parseDeeplink(segments) {
    if (!segments?.length) return null;
    const [first, second] = segments.map((s) => {
        try {
            return decodeURIComponent(s);
        } catch {
            return s;
        }
    });
    if (first.length > 1 && first.startsWith('@')) {
        return { kind: 'profile', username: first.slice(1), postId: second ?? null };
    }
    if (first === 'messages' && second?.length > 1 && second.startsWith('@')) {
        return { kind: 'conversation', username: second.slice(1) };
    }
    return { kind: 'tab', path: first };
}

// Copies an absolute deep link (e.g. '/@user') and confirms with a toast.
function copyLink(path) {
    window.navigator.clipboard
        .writeText(window.location.origin + path)
        .then(() => storage.transience.setState('internal.success', 'Link copied.'))
        .catch(() => storage.transience.setState('internal.warning', 'Could not copy the link.'));
}

// ===== guest browsing =====

// sessionStorage, not Recoil: the OAuth redirect reloads the page and wipes transient state.
const RETURN_KEY = 'loise:return';

function isGuest() {
    return !storage.transience.getValue('user');
}

// Remembers the current screen and opens the login page.
function goToLogin() {
    const containers = storage.transience.getValue('main.containers') ?? [];
    const top = containers[containers.length - 1];
    try {
        sessionStorage.setItem(
            RETURN_KEY,
            JSON.stringify({ path: top?.path, target: top?.target ?? top?.peer ?? null }),
        );
    } catch {
        // storage unavailable (private mode) — login still works, just no return
    }
    storage.transience.setState('path.gateway', 'login');
}

// Returns true when logged in. Otherwise opens the login dialog and returns false — callers bail out:
// `if (!requireLogin()) return;`
// The dialog is closed by cancelLogin() / confirmLogin().
function requireLogin() {
    if (!isGuest()) return true;
    storage.transience.setState('internal.loginPrompt', true);
    return false;
}

function cancelLogin() {
    storage.transience.setState('internal.loginPrompt', false);
}

// "Log in" pressed in the dialog.
function confirmLogin() {
    cancelLogin();
    goToLogin();
}

// Consumes the remembered screen → container stack to restore, or null.
function takeReturnContainers(base) {
    let saved = null;
    try {
        saved = JSON.parse(sessionStorage.getItem(RETURN_KEY));
        sessionStorage.removeItem(RETURN_KEY);
    } catch {
        return null;
    }
    if (saved?.path === 'profile' && saved.target?._id) return [base, createProfileProp(saved.target)];
    if (saved?.path === 'conversation' && saved.target?._id) {
        return [createMessageProp(), createConversationProp(saved.target._id, saved.target)];
    }
    const create = {
        home: createHomeProp,
        explore: createExploreProp,
        trending: createTrendingProp,
        search: createSearchProp,
        messages: createMessageProp,
        settings: createSettingProp,
        // own-profile tab (no target): the logged-in user's profile
        profile: () => createProfileProp(storage.transience.getValue('user')),
    }[saved?.path];
    return create ? [create()] : null;
}

export {
    setNavigation,
    addNavigation,
    removeNavigation,
    pushNavigation,
    popNavigation,
    createAdminProp,
    createHomeProp,
    createMessageProp,
    createSettingProp,
    createExploreProp,
    createProfileProp,
    createSearchProp,
    createUserListProp,
    createTrendingProp,
    createLegalProp,
    createConversationProp,
    parseDeeplink,
    copyLink,
    isGuest,
    goToLogin,
    requireLogin,
    cancelLogin,
    confirmLogin,
    takeReturnContainers,
};
